'use client'

import type { DirectUploadBatchProgress } from '@/shared/types/direct-upload-progress.type'

function truncateFileName(name: string, max = 44): string {
  if (name.length <= max) return name
  return `${name.slice(0, max - 1)}…`
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB'] as const
  let v = n
  let u = 0
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024
    u++
  }
  const digits = u === 0 ? 0 : v >= 100 ? 0 : v >= 10 ? 1 : 2
  return `${v.toFixed(digits)} ${units[u]}`
}

function computeOverallPercent(progress: DirectUploadBatchProgress): number {
  const { totalCount, completedCount, activeIndex, currentFileBytesUploaded, currentFileBytesTotal } =
    progress
  if (totalCount <= 0) return 0
  if (completedCount >= totalCount) return 100

  let fractionInCurrent = 0
  const active0 = activeIndex > 0 ? activeIndex - 1 : -1
  const hasByteProgress =
    active0 === completedCount &&
    currentFileBytesTotal != null &&
    currentFileBytesTotal > 0 &&
    currentFileBytesUploaded != null

  if (hasByteProgress) {
    fractionInCurrent = Math.min(1, currentFileBytesUploaded! / currentFileBytesTotal!)
  }

  return Math.min(100, Math.round(((completedCount + fractionInCurrent) / totalCount) * 100))
}

function rowPercent(
  progress: DirectUploadBatchProgress,
  filePercents: number[] | undefined,
  fileIndex: number
): number {
  if (progress.completedCount >= progress.totalCount) return 100
  if (fileIndex < progress.completedCount) return 100
  if (fileIndex > progress.completedCount) return 0
  const p = filePercents?.[fileIndex]
  if (typeof p === 'number' && Number.isFinite(p)) return Math.min(100, Math.max(0, Math.round(p)))
  return 0
}

export function UploadBatchProgressIndicator({
  progress,
  fileNames,
  filePercents,
}: {
  progress: DirectUploadBatchProgress | null
  fileNames?: string[]
  filePercents?: number[]
}) {
  if (!progress || progress.totalCount <= 0) return null

  const pct = computeOverallPercent(progress)

  let label: string
  if (progress.activeIndex > 0 && progress.activeFileName) {
    label = `Uploading ${progress.activeIndex} of ${progress.totalCount}: ${truncateFileName(progress.activeFileName)}`
  } else if (progress.completedCount >= progress.totalCount) {
    label = 'Finishing…'
  } else {
    label = 'Starting upload…'
  }

  const byteLine =
    progress.currentFileBytesTotal != null &&
    progress.currentFileBytesTotal > 0 &&
    progress.currentFileBytesUploaded != null &&
    progress.activeIndex > 0
      ? `${formatBytes(progress.currentFileBytesUploaded)} / ${formatBytes(progress.currentFileBytesTotal)}`
      : null

  return (
    <div className="space-y-3 rounded-lg border-2 border-primary/30 bg-primary/5 px-4 py-3.5 shadow-sm">
      <p className="text-sm font-medium text-foreground leading-snug break-all">{label}</p>
      <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {byteLine && <p className="text-xs font-medium text-muted-foreground leading-snug">{byteLine}</p>}
      {fileNames && fileNames.length > 1 && (
        <ul className="mt-2 max-h-44 space-y-2 overflow-y-auto pr-1" aria-label="Per-file upload progress">
          {fileNames.map((name, i) => {
            const rowPct = rowPercent(progress, filePercents, i)
            const pending = i > progress.completedCount && progress.completedCount < progress.totalCount
            return (
              <li key={`${name}-${i}`} className="space-y-1">
                <div className="flex justify-between gap-2 text-xs text-muted-foreground">
                  <span className="min-w-0 truncate" title={name}>
                    {truncateFileName(name, 36)}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {pending && rowPct === 0 ? '—' : `${rowPct}%`}
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-muted/80">
                  <div
                    className="h-full rounded-full bg-primary/90 transition-[width] duration-200 ease-out"
                    style={{ width: `${rowPct}%` }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}
      <p className="text-xs text-muted-foreground">
        Slow connection? Each file is uploaded separately; large videos may use multiple ~10 MB parts to S3 before
        they appear in the timeline.
      </p>
    </div>
  )
}
